import React from 'react';
import { Code2, ExternalLink, Github, Linkedin, Mail } from 'lucide-react';
import SideNavIndicator from '../components/SideNavIndicator';

interface Project {
    id: number;
    title: string;
    description: string;
    tags: string[];
    link?: string;
    github?: string;
}

interface HomePageProps {
    isDark: boolean;
}

const HomePage: React.FC<HomePageProps> = ({ isDark }) => {
    const projects: Project[] = [
        {
            id: 1,
            title: "Shmeevee.com",
            description: "Personal portfolio and showcase website with full-stack capabilities. Features responsive UI, AWS deployment with CI/CD pipeline, and automated deployment processes.",
            tags: ["TypeScript", "React", "Node.js", "AWS", "CI/CD"],
            link: "https://shmeevee.com",
            github: "https://github.com/ivannnito/shmeevee"
        },
        {
            id: 2,
            title: "OctoPod Notification System",
            description: "Overhauled backend notification system for OctoPrint iOS app. Transformed hardcoded system into a dynamic, scalable architecture using Python.",
            tags: ["Python", "iOS", "Backend", "API"],
            github: "https://github.com/ivannnito/OctoPrint-OctoPod"
        },
        {
            id: 3,
            title: "Mastodon Accessibility Features",
            description: "Contributed accessibility enhancements for visually impaired users. Implemented SwiftUI components to improve user experience and inclusivity.",
            tags: ["Swift", "SwiftUI", "iOS", "Accessibility"],
            github: "https://github.com/ivannnito/mastodon-ios"
        },
        {
            id: 4,
            title: "PPAP Workflow Automation",
            description: "Automated Production Part Approval Process at Onshore Technologies, reducing processing steps by 30% through robotic process automation.",
            tags: ["RPA", "Automation", "Python", "Process Optimization"]
        }
    ];

    const scrollToSection = (sectionId: string) => {
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        <div className="min-h-screen">
            <SideNavIndicator isDark={isDark} />

            {/* Hero Section */}
            <section id="home" className="min-h-screen flex items-center justify-center px-4 pt-16">
                <div className="max-w-4xl mx-auto text-center">
                    <div className="mb-8 inline-block">
                        <Code2 size={80} className="mx-auto text-cyan-500 animate-pulse" />
                    </div>
                    <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-cyan-600 via-blue-500 to-red-500 bg-clip-text text-transparent">
                        Welcome to Shmeevee
                    </h1>
                    <p className={`text-xl sm:text-2xl mb-8 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                        Developer
                    </p>
                    <p className={`text-lg mb-12 max-w-2xl mx-auto ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Building interactive experiences and API services. Explore my projects and see what I've been working on.
                    </p>
                    <button
                        onClick={() => scrollToSection('projects')}
                        className="px-8 py-3 bg-gradient-to-r from-cyan-600 to-red-500 rounded-lg font-semibold hover:shadow-lg hover:scale-105 transition-all duration-300"
                    >
                        View My Work
                    </button>
                </div>
            </section>

            {/* About Section */}
            <section id="about" className={`py-20 px-4 ${isDark ? 'bg-gray-800/50' : 'bg-white'}`}>
                <div className="max-w-4xl mx-auto">
                    <h2 className="text-4xl font-bold mb-8 text-center">About Me</h2>
                    <div className={`rounded-2xl p-8 ${isDark ? 'bg-gray-900/50' : 'bg-gray-50'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                        <p className={`text-lg mb-6 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                            My name is Ivan Dombiak, and I'm a passionate developer focused on creating simple and elegant solutions to complex problems.
                            My work spans from interactive front-end experiences to back-end API services.
                        </p>
                        <p className={`text-lg mb-6 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                            I believe in writing clean, maintainable code and building products that make a difference.
                            When I'm not coding, I'm exploring new technologies and contributing to open-source projects.
                        </p>
                        <div className="flex flex-wrap gap-3 mt-8">
                            {['TypeScript', 'React', 'Node.js', 'API Design', 'UI/UX', 'PostgreSQL', 'Java', 'Python', 'AWS', 'Swift'].map((skill) => (
                                <span
                                    key={skill}
                                    className={`px-4 py-2 rounded-full text-sm font-medium ${isDark ? 'bg-cyan-500/20 text-cyan-300' : 'bg-cyan-100 text-cyan-700'
                                        }`}
                                >
                                    {skill}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Projects Section */}
            <section id="projects" className="py-20 px-4">
                <div className="max-w-6xl mx-auto">
                    <h2 className="text-4xl font-bold mb-12 text-center">Projects</h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {projects.map((project) => (
                            <div
                                key={project.id}
                                className={`rounded-xl p-6 transition-all duration-300 hover:scale-105 hover:shadow-2xl ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
                                    }`}
                            >
                                <h3 className="text-xl font-bold mb-3">{project.title}</h3>
                                <p className={`mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                    {project.description}
                                </p>
                                <div className="flex flex-wrap gap-2 mb-4">
                                    {project.tags.map((tag) => (
                                        <span
                                            key={tag}
                                            className={`px-3 py-1 rounded-full text-xs ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'
                                                }`}
                                        >
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                                <div className="flex gap-3">
                                    {project.link && (
                                        <a
                                            href={project.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={`flex items-center gap-1 text-sm hover:text-cyan-500 transition-colors ${isDark ? 'text-gray-400' : 'text-gray-600'}`}
                                        >
                                            <ExternalLink size={16} /> Demo
                                        </a>
            )}
                                {project.github && (
                                        <a
                                            href={project.github}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={`flex items-center gap-1 text-sm hover:text-cyan-500 transition-colors ${isDark ? 'text-gray-400' : 'text-gray-600'}`}
                                        >
                                            <Github size={16} /> Code
                                        </a>
                        )}
                    </div>
                </div>
      ))}
        </div>
  </div >
    </section >

    {/* Contact Section */}
    <section id="contact" className={`py-20 px-4 ${isDark ? 'bg-gray-800/50' : 'bg-white'}`}>
        <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-8">Get In Touch</h2>
            <p className={`text-lg mb-12 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                Interested in collaborating or have a question? Feel free to reach out!
            </p>
            <div className="flex justify-center gap-6">
                <a
                    href="mailto:idombiak@gmail.com"
                    className={`p-4 rounded-full transition-all hover:scale-110 ${isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}
                >
                    <Mail size={24} />
                </a>
                <a
                    href="https://github.com/ivannnito"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`p-4 rounded-full transition-all hover:scale-110 ${isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}
                >
                    <Github size={24} />
                </a>
                <a
                    href="https://www.linkedin.com/in/ivandombiak/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`p-4 rounded-full transition-all hover:scale-110 ${isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}
                >
                    <Linkedin size={24} />
                </a>
          </div >
        </div >
      </section >
    </div >
  );
};

export default HomePage;